import type { AppConfig } from '@backstage/config';
import type { JsonObject, JsonValue } from '@backstage/types';

import { isJsonObject } from './json';
import { REGISTRY } from './registry';
import type { ProvenancedAppConfig, RegistryEntry } from './types';

/**
 * The generic context stamped on the merge-derived synthetic config. Governance
 * imports this to recognize the synthetic block (rather than a duplicated magic string)
 * and check it against the union of authorized paths.
 */
export const SYNTHETIC_CONTEXT = 'config-merge:merged';

type DeprecatedKey = { description: string; key: string };

/**
 * Read a dotted path (`app.extensions`) from a `data` object. Returns `undefined`
 * when any segment is missing or the final value is not an array.
 */
function readArrayAtPath(
  data: JsonObject,
  path: string,
): JsonValue[] | undefined {
  const segments = path.split('.');
  let current: JsonValue | undefined = data;
  for (const segment of segments) {
    if (!isJsonObject(current)) {
      return undefined;
    }
    current = current[segment];
  }
  return Array.isArray(current) ? current : undefined;
}

/** Write a value at a dotted path into `target`, creating intermediate objects. */
function writeAtPath(target: JsonObject, path: string, value: JsonValue): void {
  const segments = path.split('.');
  const leaf = segments.at(-1);
  if (leaf === undefined) {
    return;
  }
  let current = target;
  for (const segment of segments.slice(0, -1)) {
    const next = current[segment];
    if (isJsonObject(next)) {
      current = next;
    } else {
      const created: JsonObject = {};
      current[segment] = created;
      current = created;
    }
  }
  current[leaf] = value;
}

/**
 * Merge all items for a single registered path across the given configs, low->high
 * precedence, using first-appearance ordering. Combining two same-identity items uses
 * the path's own `merge` strategy (item shapes differ per path).
 */
function mergePathItems(items: JsonValue[], entry: RegistryEntry): JsonValue[] {
  // A Map preserves insertion order, which is exactly first-appearance ordering.
  const byIdentity = new Map<string, JsonValue>();

  for (const item of items) {
    const identity = entry.identity(item, SYNTHETIC_CONTEXT);
    const existing = byIdentity.get(identity);
    byIdentity.set(
      identity,
      existing === undefined ? item : entry.merge(existing, item),
    );
  }

  return [...byIdentity.values()];
}

/**
 * Union the `deprecatedKeys` of the contributing configs, deduplicated by `key`.
 * Iterates in input order (low->high precedence); the first description seen for a
 * key wins (the lowest-priority description).
 */
function unionDeprecatedKeys(configs: AppConfig[]): DeprecatedKey[] {
  // A Map preserves insertion order and keeps the first description seen per key.
  const seen = new Map<string, string>();
  for (const config of configs) {
    for (const entry of config.deprecatedKeys ?? []) {
      if (!seen.has(entry.key)) {
        seen.set(entry.key, entry.description);
      }
    }
  }
  return [...seen].map(([key, description]) => ({ description, key }));
}

/** Union the `filteredKeys` of the contributing configs, deduplicated by value. */
function unionFilteredKeys(configs: AppConfig[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const config of configs) {
    for (const key of config.filteredKeys ?? []) {
      if (!seen.has(key)) {
        seen.add(key);
        result.push(key);
      }
    }
  }
  return result;
}

/**
 * Replace stock array-replace with per-item identity merge for the registered array
 * paths (`app.extensions`, `catalog.locations`).
 *
 * The input configs are returned intact. When any registered path appears in the
 * input, exactly ONE synthetic `AppConfig` is appended last (highest priority). Its
 * `data` holds ONLY the registered paths that appeared, each at its full nested
 * key-path, with items merged on identity within that path. Unregistered paths are
 * left untouched and fall back to stock array-replace.
 *
 * Input blocks are returned INTACT, so a block's optional `path` (its provenance)
 * survives verbatim. The appended synthetic block is a {@link ProvenancedAppConfig}
 * with NO `path` (it is framework-derived): governance recognizes it by its
 * {@link SYNTHETIC_CONTEXT} and re-derives the authorized-path union itself, so the
 * fold does not need to annotate the synthetic block with per-path provenance.
 *
 * @throws {ConfigMergeError} when a registered array holds a malformed item.
 */
export function mergeAppConfigs(
  configs: ProvenancedAppConfig[],
): ProvenancedAppConfig[] {
  const syntheticData: JsonObject = {};
  const contributors: ProvenancedAppConfig[] = [];
  let anyRegisteredPath = false;

  for (const entry of REGISTRY) {
    const collected: JsonValue[] = [];
    let pathSeen = false;

    for (const config of configs) {
      const items = readArrayAtPath(config.data, entry.path);
      if (items === undefined) {
        continue;
      }
      pathSeen = true;
      if (!contributors.includes(config)) {
        contributors.push(config);
      }
      // Validate identities eagerly against the real config context, so a malformed
      // item surfaces the contributing file rather than the synthetic context.
      for (const item of items) {
        entry.identity(item, config.context);
      }
      collected.push(...items);
    }

    if (!pathSeen) {
      continue;
    }
    anyRegisteredPath = true;
    writeAtPath(syntheticData, entry.path, mergePathItems(collected, entry));
  }

  if (!anyRegisteredPath) {
    return configs;
  }

  // Preserve input order for the metadata union (low->high precedence).
  const orderedContributors = configs.filter(config =>
    contributors.includes(config),
  );

  const synthetic: ProvenancedAppConfig = {
    context: SYNTHETIC_CONTEXT,
    data: syntheticData,
    deprecatedKeys: unionDeprecatedKeys(orderedContributors),
    filteredKeys: unionFilteredKeys(orderedContributors),
  };

  return [...configs, synthetic];
}
